import * as React from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { Box, Button, TextField as MuiTextField, Typography } from '@material-ui/core';

// NoBid-hosted cookie-sync page. Loading it in a hidden iframe fires all of NoBid's
// partner user-syncs (see adserver-public src/sync.html `nobidSync(...)` calls), which
// raises bidder cookie-match rates — and therefore bid density and revenue.
export const SYNC_PIXEL_HOST = 'https://public.servenobid.com/sync.html';

// Publishers with no owning org resolve to the NoBid org (id 1), mirroring the backend
// fallback in insights service/etl/etl_base.js.
export const DEFAULT_ORG_ID = 1;

/**
 * Build the exact user-sync snippet for a publisher.
 *
 * The sync URL carries `pubid` (the publisher code) and `orgid` (the publisher's owning
 * organization id) ahead of the `redirect=` query string. The `redirect` target
 * (SSP_DOMAIN.COM) and the user-id macro ($UID) are intentional placeholders: the
 * integrator replaces SSP_DOMAIN.COM with their setuid callback URL and $UID with NoBid's
 * user-id macro. NoBid substitutes the id server-side via /getsync.
 *
 * @param {number|string} pubid  The publisher `code`.
 * @param {number|string} orgid  The publisher's `organization_id`.
 * @returns {string} A <script> tag string.
 */
export function buildSyncPixelSnippet(pubid, orgid) {
  return `<script>
  var iframe = document.createElement("iframe");
  iframe.src = "${SYNC_PIXEL_HOST}?pubid=${pubid}&orgid=${orgid}&redirect=https%3A%2F%2FSSP_DOMAIN.COM%3Fuid%3D%24UID";
  iframe.style.display = "none";
  document.body.appendChild(iframe);
</script>`;
}

/**
 * Read-only documentation block for the NoBid user-sync pixel, rendered on the publisher
 * Report Settings tab. Reads the publisher record to populate `pubid` / `orgid`, then
 * shows the copy-paste snippet plus a short explanation of which tokens the integrator
 * must replace.
 */
const SyncPixelField = () => {
  const notify = useNotify();
  const record = useRecordContext();
  const pubid = record && record.code;

  if (!pubid) {
    return (
      <Typography variant="body2">
        Save the publisher first to generate its user-sync pixel.
      </Typography>
    );
  }

  const orgid = (record && record.organization_id) || DEFAULT_ORG_ID;
  const snippet = buildSyncPixelSnippet(pubid, orgid);

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      notify('User Sync Pixel copied to clipboard', { type: 'info' });
    }).catch(() => {
      notify('Failed to copy — please copy manually', { type: 'warning' });
    });
  };

  return (
    <Box style={{ marginBottom: 16, width: '90%' }}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Place this hidden iframe on the site to run NoBid user sync. It fires NoBid's
        partner cookie-syncs, which raises bidder match rates and revenue. For a partner
        integration, replace <code>SSP_DOMAIN.COM</code> with the partner's setuid callback
        URL and <code>$UID</code> with NoBid's user-id macro.
      </Typography>
      <MuiTextField
        value={snippet}
        multiline
        fullWidth
        minRows={5}
        InputProps={{ readOnly: true }}
      />
      <Button onClick={copy} variant="outlined" style={{ marginTop: 8 }}>Copy</Button>
    </Box>
  );
};

export default SyncPixelField;
